import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { IntegrationTransactionsHeader } from "./components/IntegrationTransactionsHeader";
import { IntegrationToolbar } from "./components/IntegrationToolbar";
import { IntegrationGrid } from "./components/IntegrationGrid";
import { IntegrationEmptyState } from "./components/IntegrationEmptyState";
import { useIntegrations } from "../../../hooks/useIntegrations";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { filterIntegrations, getConnected } from "./integrationUtils";
import type { Integration, IntegrationFilter } from "./integrationTypes";

/**
 * Accounting > Integration transactions page.
 *
 * The sticky page header (breadcrumb + title + subtitle + Request
 * integration action) is rendered by AppLayout via its pageHeader slot.
 * See IntegrationTransactionsHeader, re-exported from this file so
 * authedRoutes.tsx can wire it.
 *
 * Page body:
 *   - Empty intro hero (shown ONLY when the user has zero integrations
 *     connected AND no active filter)
 *   - Toolbar (search + category chips)
 *   - Grid of cards, grouped by category, Connected section pulled up
 *     when any exist
 *
 * The empty intro and toolbar use a negative-margin wrapper to render
 * edge-to-edge against AppLayout's main padding, so their border-bottom
 * spans the full card width while the grid below stays within the
 * normal content padding.
 *
 * All data flows through hooks (useIntegrations). Standing Novala rule:
 * components do not import mock data directly.
 *
 * Click handlers today fire a "coming soon" toast specific to the brand
 * being connected. Replace with real OAuth flow per connector family in
 * follow-up PRs.
 */

// Re-export the header so authedRoutes.tsx can import it alongside the
// default page export.
export { IntegrationTransactionsHeader };

const PALETTE = {
  ink: "#0E1A1A",
  ink4: "#6B7684",
};

const TOAST_DURATION_MS = 4000;

export default function IntegrationTransactionsPage() {
  const { isMobile } = useBreakpoint();
  const { data: integrations, loading } = useIntegrations();
  const [filter, setFilter] = useState<IntegrationFilter>({
    category: "all",
    search: "",
  });
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const handleConnect = useCallback((integration: Integration) => {
    if (integration.status === "connected") {
      setToastMessage(`${integration.name} settings coming soon`);
    } else {
      setToastMessage(`${integration.name} integration coming soon`);
    }
  }, []);

  const handleLearnMore = useCallback((integration: Integration) => {
    if (integration.learnMoreUrl) {
      window.open(integration.learnMoreUrl, "_blank", "noopener,noreferrer");
    } else {
      setToastMessage(`${integration.name} documentation coming soon`);
    }
  }, []);

  const filtered = filterIntegrations(integrations, filter);
  const connectedCount = getConnected(integrations).length;
  const showEmptyIntro =
    connectedCount === 0 && filter.category === "all" && !filter.search;

  if (loading) {
    return (
      <div style={pageStyle}>
        <LoadingPlaceholder />
      </div>
    );
  }

  // Pull the empty intro + toolbar edge-to-edge, cancelling AppLayout's
  // main padding so their border-bottoms span the full card width. The
  // grid below stays within normal content padding.
  const edgeStyle: CSSProperties = {
    margin: isMobile ? "-16px -16px 0" : "-28px -32px 0",
  };

  const gridWrapStyle: CSSProperties = {
    paddingTop: isMobile ? 20 : 24,
  };

  return (
    <div style={pageStyle}>
      <div style={edgeStyle}>
        {showEmptyIntro && <IntegrationEmptyState />}
        <IntegrationToolbar filter={filter} onFilterChange={setFilter} />
      </div>

      <div style={gridWrapStyle}>
        <IntegrationGrid
          integrations={filtered}
          onConnect={handleConnect}
          onLearnMore={handleLearnMore}
        />
      </div>

      {toastMessage && <Toast message={toastMessage} />}
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div style={loadingStyle} role="status" aria-live="polite">
      Loading integrations...
    </div>
  );
}

const pageStyle: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  background: "#ffffff",
};

const loadingStyle: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "calc(100vh - 180px)",
  fontSize: 13,
  color: PALETTE.ink4,
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};